import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import BackLink from "@/components/BackLink";
import { PageHeader } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { inr, formatDate } from "@/lib/utils2";

export default function MaterialLedger() {
  const { id } = useParams();
  const [material, setMaterial] = useState(null);
  const [trucks, setTrucks] = useState([]);
  const [parties, setParties] = useState([]);

  useEffect(() => {
    (async () => {
      const [m, t, p] = await Promise.all([
        api.get("/materials"),
        api.get("/trucks"),
        api.get("/parties"),
      ]);
      setMaterial(m.data.find((x) => x.id === id));
      setTrucks(t.data.filter((x) => x.material_id === id));
      setParties(p.data);
    })();
  }, [id]);

  const partyName = (pid) => parties.find((p) => p.id === pid)?.name || "—";
  const totals = trucks.reduce((a, t) => ({
    weight: a.weight + (t.material_weight || 0),
    bill: a.bill + (t.material_bill_amount || 0),
    purchase: a.purchase + (t.material_purchase_amount || 0),
    profit: a.profit + ((t.material_bill_amount || 0) - (t.material_purchase_amount || 0)),
  }), { weight: 0, bill: 0, purchase: 0, profit: 0 });

  return (
    <div className="p-6 md:p-10 lg:p-12 max-w-[1300px] mx-auto" data-testid="material-ledger-page">
      <BackLink to="/" label="Back" className="mb-4" testId="material-ledger-back" />
      <PageHeader title={material?.name || "Material"} subtitle={`HSN: ${material?.hsn || "—"} · Unit: ${material?.unit || "MT"}`} />

      <div className="grid grid-cols-4 gap-4 mb-6">
        <KPI label="Trips" value={trucks.length} />
        <KPI label="Total Weight" value={`${totals.weight.toFixed(2)} MT`} />
        <KPI label="Bill Amount" value={inr(totals.bill)} />
        <KPI label="Profit" value={inr(totals.profit)} accent="text-brand-forest" />
      </div>

      <Card className="rounded-sm border-brand-line shadow-none overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/60 text-[10px] uppercase tracking-[0.15em] text-brand-muted">
            <tr><th className="text-left px-5 py-3">Date</th><th className="text-left px-3 py-3">Vehicle</th><th className="text-left px-3 py-3">Party</th><th className="text-right px-3 py-3">Wgt</th><th className="text-right px-3 py-3">Purchase</th><th className="text-right px-3 py-3">Bill</th><th className="text-right px-5 py-3">Profit</th></tr>
          </thead>
          <tbody>
            {trucks.length === 0 && <tr><td colSpan={7} className="px-5 py-10 text-center text-brand-muted text-sm">No movements yet.</td></tr>}
            {trucks.map((t) => (
              <tr key={t.id} className="border-t border-brand-line">
                <td className="px-5 py-2.5">{formatDate(t.entry_date)}</td>
                <td className="px-3 py-2.5 font-medium">{t.vehicle_number}</td>
                <td className="px-3 py-2.5">{partyName(t.party_id)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{t.material_weight}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{inr(t.material_purchase_amount)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{inr(t.material_bill_amount)}</td>
                <td className="px-5 py-2.5 text-right tabular-nums text-brand-forest">{inr((t.material_bill_amount || 0) - (t.material_purchase_amount || 0))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function KPI({ label, value, accent }) {
  return (
    <Card className="rounded-sm border-brand-line shadow-none p-5">
      <div className="text-[10px] uppercase tracking-[0.22em] text-brand-muted">{label}</div>
      <div className={`font-serif text-3xl mt-2 tabular-nums ${accent || "text-brand-ink"}`}>{value}</div>
    </Card>
  );
}
